/*
 * Copyright (c) 2020 CalAmp Corp.  All Rights Reserved
 */
import produce from 'immer';
import isundefined from 'lodash.isundefined';
// eslint-disable-next-line no-restricted-imports
import {NextRouter} from 'next/router';
import {get, isEUProduction} from '@ion/core/src/environment/environment';
import {translate} from '@ion/core/src/locale/locale';
import UserPreferences from '@ion/core/src/preferences/UserPreferences';
import {hasRole} from '@ion/core/src/security/helpers';
import {GenericObject, User} from '@ion/core/src/types/entities';
import Features, {
  ADD_ONS_CRASHBOXX,
  ADD_ONS_ELD,
  ADD_ONS_ION_VISION,
  ADD_ONS_ION_VISION_EVENTS,
  ADMIN,
  ADMIN_ALERTS_PAGE,
  ADMIN_ASSETS_PAGE,
  ADMIN_DEVICES_PAGE,
  ADMIN_DRIVER_SCORECARD,
  ADMIN_GROUPS_PAGE,
  ADMIN_IO_PROFILES_PAGE,
  ADMIN_LANDMARK_GEOFENCE_PAGE,
  ADMIN_MAINTENANCE_PAGE,
  ADMIN_OPERATORS_PAGE,
  ADMIN_ROLES_PAGE,
  ADMIN_ROUTES_PAGE,
  ADMIN_SCHEDULES_PAGE,
  ADMIN_USERS_PAGE,
  ANALYTICS_PAGE,
  CRASHBOXX,
  DASHBOARD_PAGE,
  ELD,
  EVENTS,
  EVENTS_GEOFENCES_PAGE,
  EVENTS_NOTIFICATIONS_PAGE,
  MAINTENANCE,
  MAINTENANCE_DASHBOARD,
  MAINTENANCE_PAGE,
  REPORT_MANAGER_PAGE,
  TRACKING_HVS_FEATURES,
  TRACKING_PAGE,
  TRACKING_PAGE2,
  TRACKING_SUPPLY_CHAIN,
  TRIP,
  TRIP_LIST,
  VEHICLE_INSPECTIONS,
  VEHICLE_INSPECTIONS_RESULTS,
  VEHICLE_INSPECTIONS_SETUP,
  VISION,
  VISION_ASSETS,
  VISION_BETA,
  VISION_COACHING,
  VISION_CONFIGURATIONS,
  VISION_DRIVERS,
  VISION_HEALTH,
  VISION_HOME,
  VISION_LIVE,
  VISION_REPORTS
} from '@/security/Features';
import {SET_GLOBAL_GROUP} from '@/store/actions/global-group-chooser';
import {
  APPLICATION_INITIALIZATION_COMPLETE
} from '@/store/actions/initialization';
import {
  getFallbackPageId,
  PAGE_SECURITY_INITIALIZED,
  SET_ACTIVE_INDEX,
  SET_DEEP_LINK_RESTRICTION,
  SET_GRID_OPTIONS,
  SET_IGNORE_QUERY,
  SET_LAYOUT_OFFSET_HEIGHT,
  SET_MOBILE_LIST_VISIBLE,
  SET_NAV_COLLAPSED_STATUS,
  SET_PAGE,
  SET_PREVIOUS_PAGE,
  SET_SIDE_SHEET_STATUS,
  TOGGLE_SECONDARY_NAV,
  TOGGLE_SIDESHEET_ARROW,
  TOGGLE_USER_PROFILE_VIEW
} from '@/store/actions/nav';
import {Page} from '@/types/pages';
import {
  getPersistedRoute,
  getRoute,
  getRouteByFeature,
  isRoute,
  isValidPath,
  Route
} from '../../routes/RouteDefinitions';
import {getDefaultPageId} from '../../routes/RouteSecurity';

/**
 * @authorizedPages
 *
 * Provides a utility which traverses all page definitions are returns only the
 * pages to which the user is authorized access.
 */
export const authorizedPages = (pages: Page[]): Page[] => {
  return pages.filter(({pages, isAuthorized}: Page) => {
    if (!pages) {
      return isAuthorized;
    }
    return authorizedPages(pages as Page[]);
  });
};

/**
 * Defines a mapping of iOn Feature ids to their corresponding translation
 * entry keys.
 */
const pageFeatureIdMap = {
  [DASHBOARD_PAGE]              : 'dashboard',
  [TRACKING_PAGE]               : 'tracking',
  [TRACKING_PAGE2]              : 'tracking2',
  [TRIP]                        : 'trip',
  [TRIP_LIST]                   : 'tripList',
  [TRACKING_HVS_FEATURES]       : 'reports',
  [ANALYTICS_PAGE]              : 'analytics',
  [REPORT_MANAGER_PAGE]         : 'reportManager',
  [MAINTENANCE_PAGE]            : 'maintenance',
  [VEHICLE_INSPECTIONS]         : 'inspections',
  [VEHICLE_INSPECTIONS_RESULTS] : 'inspectionsResults',
  [VEHICLE_INSPECTIONS_SETUP]   : 'inspectionsSetup',
  [ADD_ONS_CRASHBOXX]           : 'crashboxx',
  [EVENTS]                      : 'events',
  [EVENTS_GEOFENCES_PAGE]       : 'geofenceEvents',
  [EVENTS_NOTIFICATIONS_PAGE]   : 'notificationEvents',
  [ADMIN]                       : 'admin',
  [ADMIN_ASSETS_PAGE]           : 'adminAssets',
  [ADMIN_DEVICES_PAGE]          : 'adminDevices',
  [ADMIN_OPERATORS_PAGE]        : 'adminDrivers',
  [ADMIN_GROUPS_PAGE]           : 'adminGroups',
  [ADMIN_ROUTES_PAGE]           : 'adminRoutes',
  [ADMIN_ROLES_PAGE]            : 'adminRoles',
  [ADMIN_USERS_PAGE]            : 'adminUsers',
  [ADMIN_ALERTS_PAGE]           : 'adminNotifications',
  [ADMIN_MAINTENANCE_PAGE]      : 'adminMaintenance',
  [ADMIN_SCHEDULES_PAGE]        : 'adminSchedules',
  [ADMIN_IO_PROFILES_PAGE]      : 'adminIoprofiles',
  [ADD_ONS_ELD]                 : 'eld',
  [MAINTENANCE]                 : 'maintenancedashboard',
  [CRASHBOXX]                   : 'add_ons_crashboxx',
  [VISION]                      : 'add_ons_vision',
  [ELD]                         : 'add_ons_eld',
  [ADD_ONS_ION_VISION]          : 'vision',
  [VISION_BETA]                 : 'visionBeta',
  [ADD_ONS_ION_VISION_EVENTS]   : 'visionevents'
};

/**
 * @private
 *
 * Provides a utility which updates all authorized page names and labels with
 * the corresponding translation for the page's feature id.
 */
const translateAuthorizedPages = (pages: Page[]): Page[] => {
  return pages.map((page: Page) => {
    page.name = page.label = translate(`nav:side.${pageFeatureIdMap[page.id]}`);
    return page;
  });
};

/**
 * Provides a utility which returns the navigation item index for the given
 * route.
 */
const getIndex = (pages: Page[], page: string): number => {
  let index: number;
  pages.find(({pages: subpages, route}: Page, i) => {
    const match    = getRoute(route) === getRoute(page as Route);
    const hasChild = subpages && !isundefined(getIndex(subpages as Page[], page));
    if (match || hasChild) {
      index = i;
      return true;
    }
  });
  return index;
};

export const testFallbackPageId = (pages: Page[]) => getFallbackPageId(pages);

/**
 * @private 
 */
const resolveDefaultPageId = () => {
  const defaultPageId = getDefaultPageId(UserPreferences.get('defaultPageId') || TRACKING_PAGE2);
  if (defaultPageId === TRACKING_PAGE) {
    UserPreferences.set('defaultPageId', TRACKING_PAGE2, true);
    return TRACKING_PAGE2;
  }
  return defaultPageId;
};

/**
 * The secured, feature-based entry page - independent of whatever page
 * happens to be currently loaded. This is what the side nav logo/brand
 * should return to, as distinct from `defaultPage` below (which resumes
 * whatever page the user is currently on, e.g. across a refresh).
 *
 * @private
 */
const getSecuredEntryPage = (pages: Page[]): Route => {
  const defaultPageId = resolveDefaultPageId();
  return getRouteByFeature(
    Features.hasFeature(defaultPageId) ? defaultPageId : getFallbackPageId(pages)
  );
};

/**
 * @private
 */
const getDefaultPage = (route: NextRouter, fromLogin: boolean, pages: Page[]): Route => {
  const defaultPage = getSecuredEntryPage(pages);
  if (!fromLogin) {
    const currentRoute = route ?? window?.location;
    const current = isValidPath({
      pathname : currentRoute?.pathname
    }) ? currentRoute?.pathname : null;
    return (current || getPersistedRoute() || defaultPage) as Route;
  }
  return defaultPage;
};

/**
 * @private
 */
const setAvailablePages = (draft: NavState, {route, user}) => {
  const authorized = translateAuthorizedPages(
    Features.getPermittedPages({pages: pages(user)}) as Page[]
  );

  const secondary     = authorized.find((page: Page) => page.id === ADMIN);
  draft.pages         = authorized as Page[];
  draft.defaultPage   = getDefaultPage(route, isRoute(Route.LOGIN), draft.pages);
  draft.appEntryPage  = getSecuredEntryPage(draft.pages);
  draft.selectedIndex = getIndex(draft.pages, draft.defaultPage);

  if (secondary) {
    draft.secondaryNav.pages = secondary.pages as Page[];
  }
};

// defines the shape of this reducers state ...
export type NavState = {
  pages: Page[],
  defaultPage: Route
  appEntryPage: Route
  currentPage: string;
  previousPage: string;
  selectedIndex: number;
  activeIndex: number;
  isInternalRoute: boolean;
  isUserProfileView: boolean;
  ignoreQuery: boolean;
  pageSecurityInitialized: boolean;
  collapsed: boolean;
  secondaryNav: {
    pages: Page[];
    show: boolean;
  }
  sideSheet: {
    activeSideSheet: null | string;
    desktopSideSheetOpen: boolean;
    mobileSideSheetOpen: boolean;
  },
  isSideSheetArrowVisible: boolean;
  mobileListVisible: boolean;
  gridOptions: GenericObject;
  deepLinkRestricted: boolean;
  layoutOffsetHeight: number;
};

// defines the initial state of this reducer ...
export const initialState: NavState = {
  pages                   : [],
  defaultPage             : null,
  appEntryPage            : null,
  currentPage             : null,
  previousPage            : null,
  selectedIndex           : NaN,
  activeIndex             : -1,
  isInternalRoute         : false,
  isUserProfileView       : false,
  ignoreQuery             : false,
  pageSecurityInitialized : false,
  secondaryNav            : {
    pages : [],
    show  : false
  },
  sideSheet : {
    activeSideSheet      : null,
    desktopSideSheetOpen : false,
    mobileSideSheetOpen  : false
  },
  isSideSheetArrowVisible : true,
  mobileListVisible       : true,
  gridOptions             : null,
  deepLinkRestricted      : false,
  layoutOffsetHeight      : null,
  collapsed               : false
};

const tracking2Nav = {
  id            : TRACKING_PAGE2,
  route         : Route.TRACKING2,
  isActive      : true,
  isAuthorized  : false,
  showSideSheet : false,
  isPreview     : true,
  secondaryNav  : true,
  pages         : [{
    id            : TRACKING_PAGE2,
    route         : Route.TRACKING2,
    labelId       : Route.TRACKING2,
    isActive      : true,
    isAuthorized  : false,
    showSideSheet : false,
    isPreview     : true
  }]
};

// Return Tracking Nav based on the access of the user.
const getTrackingNav = () => {
  const hasTrackingFeature  = Features.hasFeature(TRACKING_PAGE);
  const hasTracking2Feature = Features.hasFeature(TRACKING_PAGE2);
  if (hasTrackingFeature || hasTracking2Feature) {
    return tracking2Nav;
  }
  return {};
};

const getTripNav = () => {
  const hasTripFeature     = Features.hasFeature(TRIP);
  const hasTripListFeature = Features.hasFeature(TRIP_LIST);

  if (!hasTripFeature || !hasTripListFeature) {
    return {};
  }

  return {
    id            : TRIP,
    route         : Route.TRIP,
    isActive      : true,
    isAuthorized  : false,
    showSideSheet : false,
    isPreview     : true,
    secondaryNav  : true,
    pages         : [{
      id            : TRIP_LIST,
      route         : Route.TRIP_LIST,
      isActive      : true,
      isAuthorized  : false,
      showSideSheet : false,
      isPreview     : true
    }]
  };
};

/**
 * Defines all iOn Suite application pages definitions. Upon post initialization
 * (after the user has authenticated), the pages are passed through the Features
 * API which filters them based on those to which the User is authorized to view
 * based on their given Role.
 *
 * In addition, when initially referenced, the isPreview value fro specific pages
 * will be set based on the value of the "featureFlagsEnabled" entry from the
 * applications environment.json document. This allow for externally enabling
 * and disabling of flagged features.
 */
export const pages = (user: User): Page[] => {
  const featureFlagsEnabled = get('featureFlagsEnabled');
  const restrictedRoleIds   = get('restrictedRoleIds');
  // sisense feature is for users who have a Super Admin role and have an access dashboard page...
  const hasSisenseFeature = featureFlagsEnabled &&
    hasRole(user?.roles, restrictedRoleIds) && Features.has(DASHBOARD_PAGE);
  const hasVision           = featureFlagsEnabled || Features.hasFeature(ADD_ONS_ION_VISION);
  const hasCrashboxFeature  = Features.hasFeature(ADD_ONS_CRASHBOXX);
  const hasCargoTrackingAccess = Features.hasFeature(TRACKING_SUPPLY_CHAIN);

  const dashboardSecondaryNav = {
    ...(hasSisenseFeature ? {
      id            : DASHBOARD_PAGE,
      route         : Route.SISENCE_DASHBOARD,
      isActive      : true,
      isAuthorized  : false,
      showSideSheet : false,
      isPreview     : true
    } : {})
  } as unknown as Page;

  const hasDashboardSecondaryNav = (
    Features.hasSafety() ||
    Features.hasSafetyBasic() ||
    Features.hasDevices() ||
    Features.hasDriverDashboardFeature() ||
    Features.hasUtilization() ||
    hasSisenseFeature || hasCrashboxFeature
  );

  const hasReportSecondaryNav = (
    Features.hasFeature(ANALYTICS_PAGE) ||
    Features.hasFeature(REPORT_MANAGER_PAGE) ||
    Features.hasFeature(TRACKING_HVS_FEATURES)
  );

  const hasMaintenanceFeature = (
    Features.hasFeature(MAINTENANCE_PAGE) && Features.hasFeature(MAINTENANCE_DASHBOARD)
  );

  const trackingNav = getTrackingNav();
  const tripNav     = getTripNav();

  return [{
    id            : DASHBOARD_PAGE,
    route         : Route.FLEET_DASHBOARD,
    labelId       : Route.DASHBOARD,
    isActive      : true,
    isAuthorized  : false,
    showSideSheet : false,
    isPreview     : true,
    secondaryNav  : hasDashboardSecondaryNav,
    ...(hasDashboardSecondaryNav ? {
      pages : [{
        id            : DASHBOARD_PAGE,
        route         : Route.FLEET_DASHBOARD,
        isActive      : true,
        isAuthorized  : false,
        showSideSheet : false,
        isPreview     : true
      }, {
        id            : ADD_ONS_CRASHBOXX,
        route         : Route.CRASHBOX_DASHBOARD,
        isActive      : true,
        isAuthorized  : false,
        showSideSheet : false,
        isPreview     : true
      },
      dashboardSecondaryNav]
    } : dashboardSecondaryNav)
  },
  {
    ...trackingNav as Page
  },
  {
    ...(hasVision ? {
      id            : VISION,
      route         : Route.VISION,
      isActive      : true,
      isAuthorized  : false,
      showSideSheet : false,
      isPreview     : true,
      secondaryNav  : true,
      pages         : [{
        id            : ADD_ONS_ION_VISION,
        route         : Route.VISION_LIBRARY,
        labelId       : 'vision',
        isActive      : true,
        isAuthorized  : false,
        showSideSheet : false,
        isPreview     : true
      }, {
        ...(featureFlagsEnabled ? {
          id            : ADD_ONS_ION_VISION_EVENTS,
          route         : Route.VISION_EVENTS,
          labelId       : 'visionevents',
          isActive      : true,
          isAuthorized  : false,
          showSideSheet : false,
          isPreview     : true
        } : {})
      }, {
        id            : VISION_HOME,
        route         : Route.VISION_HOME,
        labelId       : 'visionHome',
        isActive      : true,
        isAuthorized  : false,
        showSideSheet : false,
        isPreview     : true
      }, {
        id            : VISION_COACHING,
        route         : Route.VISION_COACHING,
        labelId       : 'visionCoaching',
        isActive      : true,
        isAuthorized  : false,
        showSideSheet : false,
        isPreview     : true
      }, {
        id            : VISION_LIVE,
        route         : Route.VISION_LIVE,
        labelId       : 'visionLive',
        isActive      : true,
        isAuthorized  : false,
        showSideSheet : false,
        isPreview     : true
      }, {
        id            : VISION_REPORTS,
        route         : Route.VISION_REPORTS,
        labelId       : 'visionReports',
        isActive      : true,
        isAuthorized  : false,
        showSideSheet : false,
        isPreview     : true
      }, {
        id            : VISION_HEALTH,
        route         : Route.VISION_HEALTH,
        labelId       : 'visionHealth',
        isActive      : true,
        isAuthorized  : false,
        showSideSheet : false,
        isPreview     : true
      }, {
        id            : VISION_DRIVERS,
        route         : Route.VISION_DRIVERS,
        labelId       : 'visionDrivers',
        isActive      : true,
        isAuthorized  : false,
        showSideSheet : false,
        isPreview     : true
      }, {
        id            : VISION_ASSETS,
        route         : Route.VISION_ASSETS,
        labelId       : 'visionAssets',
        isActive      : true,
        isAuthorized  : false,
        showSideSheet : false,
        isPreview     : true
      }, {
        id            : VISION_CONFIGURATIONS,
        route         : Route.VISION_CONFIGURATIONS,
        labelId       : 'visionConfigurations',
        isActive      : true,
        isAuthorized  : false,
        showSideSheet : false,
        isPreview     : true
      }]
    } : {}) as unknown as Page
  },
  {
    id            : null,
    route         : Route.ISSUES,
    isActive      : false,
    isAuthorized  : false,
    showSideSheet : false,
    isPreview     : false
  }, {
    id            : EVENTS,
    route         : Route.EVENTS,
    isActive      : true,
    isAuthorized  : false,
    showSideSheet : false,
    isPreview     : true,
    secondaryNav  : true,
    pages         : [{
      id            : EVENTS_GEOFENCES_PAGE,
      route         : Route.EVENTS_GEOFENCES,
      labelId       : 'eventsGeofences',
      isActive      : true,
      isAuthorized  : false,
      showSideSheet : false,
      isPreview     : true
    }, {
      id            : EVENTS_NOTIFICATIONS_PAGE,
      route         : Route.EVENTS_NOTIFICATIONS,
      labelId       : 'eventsNotifications',
      isActive      : true,
      isAuthorized  : false,
      showSideSheet : false,
      isPreview     : true
    }]
  }, {
    id            : TRACKING_HVS_FEATURES,
    route         : Route.ANALYTICS,
    isActive      : true,
    isAuthorized  : false,
    showSideSheet : false,
    isPreview     : true,
    secondaryNav  : hasReportSecondaryNav,
    ...(hasReportSecondaryNav ? {
      pages : [{
        id            : ANALYTICS_PAGE,
        route         : Route.ANALYTICS_DASHBOARDS,
        labelId       : 'analyticsDashboards',
        isActive      : true,
        isAuthorized  : false,
        showSideSheet : false,
        isPreview     : true
      }, {
        id            : REPORT_MANAGER_PAGE,
        route         : Route.ANALYTICS_SCHEDULER,
        isActive      : true,
        isAuthorized  : false,
        showSideSheet : false,
        isPreview     : true
      }, {
        ...(hasCargoTrackingAccess ? {
          id            : TRACKING_SUPPLY_CHAIN,
          route         : Route.REPORTS,
          labelId       : 'gatewayHistory',
          isActive      : true,
          isAuthorized  : false,
          showSideSheet : false,
          isPreview     : true
        } : {} as unknown as Page)
      }]
    }: {} as unknown as Page)
  }, {
    ...(hasCrashboxFeature ? {
      id            : CRASHBOXX,
      route         : Route.CRASHBOXX,
      isActive      : true,
      isAuthorized  : false,
      showSideSheet : false,
      isPreview     : true,
      secondaryNav  : true,
      pages         : [{
        id            : ADD_ONS_CRASHBOXX,
        route         : Route.CRASHBOXX,
        labelId       : 'crashboxx',
        isActive      : true,
        isAuthorized  : false,
        showSideSheet : false,
        isPreview     : true
      }]
    } : {}) as unknown as Page
  }, {
    id            : VEHICLE_INSPECTIONS,
    route         : Route.INSPECTIONS,
    isActive      : true,
    isAuthorized  : false,
    showSideSheet : false,
    isPreview     : true,
    secondaryNav  : true,
    pages         : [{
      id            : VEHICLE_INSPECTIONS_SETUP,
      route         : Route.INSPECTIONS_SETUP,
      labelId       : 'inspectionsSetup',
      isActive      : true,
      isAuthorized  : false,
      showSideSheet : false,
      isPreview     : true
    }, {
      id            : VEHICLE_INSPECTIONS_RESULTS,
      route         : Route.INSPECTIONS_RESULTS,
      labelId       : 'inspectionsResults',
      isActive      : true,
      isAuthorized  : false,
      showSideSheet : false,
      isPreview     : true
    }]
  }, {
    ...(hasMaintenanceFeature ? {
      id            : MAINTENANCE,
      route         : Route.MAINTENANCE_DASHBOARD,
      isActive      : true,
      isAuthorized  : false,
      showSideSheet : false,
      isPreview     : true,
      secondaryNav  : true,
      pages         : [{
        id            : MAINTENANCE_DASHBOARD,
        route         : Route.MAINTENANCE_DASHBOARD,
        labelId       : 'maintenance',
        isActive      : true,
        isAuthorized  : false,
        showSideSheet : false,
        isPreview     : true
      }]
    } : {}) as unknown as Page
  }, {
    ...(!isEUProduction() ? {
      id            : ELD,
      route         : Route.ELD,
      isActive      : true,
      isAuthorized  : false,
      showSideSheet : false,
      isPreview     : true,
      secondaryNav  : true,
      pages         : [{
        id            : ADD_ONS_ELD,
        route         : Route.ELD,
        labelId       : 'eld',
        isActive      : true,
        isAuthorized  : false,
        showSideSheet : false,
        isPreview     : true
      }]
    } : {}) as unknown as Page
  }, {
    ...tripNav as Page
  }, {
    id            : ADMIN,
    route         : Route.ADMIN,
    isActive      : true,
    isAuthorized  : false,
    showSideSheet : false,
    isPreview     : true,
    secondaryNav  : true,
    pages         : [{
      id            : ADMIN_ASSETS_PAGE,
      route         : Route.ADMIN_ASSETS,
      isActive      : true,
      isAuthorized  : false,
      showSideSheet : false,
      isPreview     : true
    }, {
      id            : ADMIN_DEVICES_PAGE,
      route         : Route.ADMIN_DEVICES,
      isActive      : true,
      isAuthorized  : false,
      showSideSheet : false,
      isPreview     : true
    }, {
      id            : ADMIN_OPERATORS_PAGE,
      route         : Route.ADMIN_DRIVERS,
      isActive      : true,
      isAuthorized  : false,
      showSideSheet : false,
      isPreview     : true
    }, {
      id            : ADMIN_GROUPS_PAGE,
      route         : Route.ADMIN_GROUPS,
      isActive      : true,
      isAuthorized  : false,
      showSideSheet : false,
      isPreview     : true
    }, {
      id            : ADMIN_ROUTES_PAGE,
      route         : Route.ADMIN_ROUTES,
      isActive      : true,
      isAuthorized  : false,
      showSideSheet : false,
      isPreview     : true
    }, {
      id            : ADMIN_ROLES_PAGE,
      route         : Route.ADMIN_ROLES,
      isActive      : true,
      isAuthorized  : false,
      showSideSheet : false,
      isPreview     : true
    }, {
      id            : ADMIN_USERS_PAGE,
      route         : Route.ADMIN_USERS,
      isActive      : true,
      isAuthorized  : false,
      showSideSheet : false,
      isPreview     : true
    }, {
      id            : ADMIN_LANDMARK_GEOFENCE_PAGE,
      route         : Route.ADMIN_GEOFENCES,
      isActive      : true,
      isAuthorized  : false,
      showSideSheet : false,
      isPreview     : true
    }, {
      id            : ADMIN_ALERTS_PAGE,
      route         : Route.ADMIN_NOTIFICATIONS,
      isActive      : true,
      isAuthorized  : false,
      showSideSheet : false,
      isPreview     : true
    }, {
      id            : ADMIN_MAINTENANCE_PAGE,
      route         : Route.ADMIN_MAINTENANCE,
      isActive      : true,
      isAuthorized  : false,
      showSideSheet : false,
      isPreview     : true
    }, {
      id            : ADMIN_SCHEDULES_PAGE,
      route         : Route.ADMIN_SCHEDULES,
      isActive      : true,
      isAuthorized  : false,
      showSideSheet : false,
      isPreview     : true
    }, {
      id            : ADMIN_IO_PROFILES_PAGE,
      route         : Route.ADMIN_IOPROFILES,
      isActive      : true,
      isAuthorized  : false,
      showSideSheet : false,
      isPreview     : true
    }, {
      id            : ADMIN_DRIVER_SCORECARD,
      route         : Route.ADMIN_DRIVER_SCORING,
      isActive      : true,
      isAuthorized  : false,
      showSideSheet : false,
      isPreview     : true
    }]
  }];
};

// defines the reducers ...
const reducers = {
  [SET_PAGE] : (draft: NavState, {page}) => {
    draft.selectedIndex   = getIndex(draft.pages, getRoute(page));
    draft.previousPage    = draft.currentPage;
    draft.currentPage     = getRoute(page);
    draft.isInternalRoute = true;
  },
  [SET_SIDE_SHEET_STATUS] : (draft: NavState, page) => {
    draft.sideSheet = {...draft.sideSheet, ...page.data};
  },
  [APPLICATION_INITIALIZATION_COMPLETE] : setAvailablePages,
  [SET_GLOBAL_GROUP]                    : setAvailablePages,
  [PAGE_SECURITY_INITIALIZED]           : (draft: NavState) => {
    draft.pageSecurityInitialized = true;
  },
  [TOGGLE_SECONDARY_NAV] : ({secondaryNav}: NavState, {show}) => {
    secondaryNav.show = show;
  },
  [TOGGLE_USER_PROFILE_VIEW] : (draft, {view}) => {
    draft.isUserProfileView = view;
  },
  [SET_PREVIOUS_PAGE] : (draft, {route}) => {
    draft.previousPage = route;
  },
  [SET_IGNORE_QUERY] : (draft, {value}) => {
    draft.ignoreQuery = value;
  },
  [TOGGLE_SIDESHEET_ARROW] : (draft, {isVisible}) => {
    draft.isSideSheetArrowVisible = isVisible;
  },
  [SET_MOBILE_LIST_VISIBLE] : (draft: NavState, {isVisible}) => {
    draft.mobileListVisible = isVisible;
  },
  [SET_ACTIVE_INDEX] : (draft, {index}) => {
    draft.activeIndex = index;
  },
  [SET_GRID_OPTIONS] : (draft, {options}) => {
    draft.gridOptions = options;
  },
  [SET_DEEP_LINK_RESTRICTION] : (draft: NavState, {restriction}) => {
    draft.deepLinkRestricted = restriction;
  },
  [SET_LAYOUT_OFFSET_HEIGHT] : (draft: NavState, {height}) => {
    draft.layoutOffsetHeight = height;
  },
  [SET_NAV_COLLAPSED_STATUS] : (draft: NavState, {collapsed}) => {
    draft.collapsed = collapsed;
  }
};

// defines all reducers for actions of interest to this reducer ...
export default produce((draft: NavState = initialState, action) => {
  reducers?.[action.type]?.(draft, action);
  return draft;
});
